import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col, Card, CardBody, Container, Form, FormGroup, Input } from "reactstrap";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import Select, { components } from "react-select";
import { toast } from "react-toastify";

import BootstrapTable from "../../../../share-components/table/BootstrapTable";
import Breadcrumb from "../../../../layout/breadcrumb";

import withReducer from "../../../../store/withReducer";
import reportReducer from "../../index";

import {
    getPaymentSourceDetail,
    exportPaymentSourceDetail,
    recheckPayoutLines,
} from "../store/payoutReportSlice";

import { formatterCurrency } from "../../../../utils/common";

const TYPE_OPTIONS = [
    { value: 'CHARGE',             label: 'Charge' },
    { value: 'REFUND',             label: 'Refund' },
    { value: 'CHARGEBACK',         label: 'Chargeback' },
    { value: 'ADJUSTMENT',         label: 'Adjustment' },
    { value: 'PAYOUT',             label: 'Payout' },
    { value: 'DISPUTE_WITHDRAWAL', label: 'Dispute Withdrawal' },
    { value: 'DISPUTE_REVERSAL',   label: 'Dispute Reversal' },
    { value: 'REFUND_FAILURE',     label: 'Refund Failure' },
];

const MATCH_STATUS_OPTIONS = [
    { value: 'matched',         label: 'Matched' },
    { value: 'amount_mismatch', label: 'Amount Mismatch' },
    { value: 'unmatched',       label: 'Unmatched' },
];

const DATE_MATCH_STATUS_OPTIONS = [
    { value: 'matched',       label: 'Matched' },
    { value: 'date_mismatch', label: 'Date Mismatch' },
    { value: 'unmatched',     label: 'Unmatched' },
    { value: 'not_checked',   label: 'Not Checked' },
];

const MATCH_STATUS_CFG = {
    matched:         { color: 'success', label: 'Matched' },
    amount_mismatch: { color: 'warning', label: 'Amount Mismatch' },
    unmatched:       { color: 'danger',  label: 'Unmatched' },
};

const DATE_MATCH_STATUS_CFG = {
    matched:       { color: 'success', label: 'Matched' },
    date_mismatch: { color: 'warning', label: 'Date Mismatch' },
    unmatched:     { color: 'danger',  label: 'Unmatched' },
};

const PaymentSourceDetails = () => {
    const { id } = useParams();

    const payoutReportState = useSelector(({ report }) => report.payoutReport);
    const dispatch = useDispatch();

    const matchSummary = payoutReportState.paymentSourceDetailMatchSummary;

    const [loading, setLoading]               = useState(true);
    const [recheckLoading, setRecheckLoading] = useState(false);
    const [exportBtnDisabled, setExportBtnDisabled] = useState(false);
    const [unsupportedGateway, setUnsupportedGateway] = useState(null);
    const [searchTimer, setSearchTimer]       = useState(null);
    const [lastFilters, setLastFilters]       = useState(null);
    const [typeFilter, setTypeFilter]         = useState(null);
    const [matchStatusFilter, setMatchStatusFilter] = useState(null);
    const [dateMatchStatusFilter, setDateMatchStatusFilter] = useState(null);

    const [filter, setFilter] = useState({
        page: 1,
        pageSize: 10,
        search: '',
        type: null,
        match_status: null,
        date_match_status: null,
    });

    const dataMenu = [
        { type: 0, text: 'Reports' },
        { type: 1, text: 'Payments Sources', link: '/report/payments/payouts' },
        { type: 0, text: 'Payment Source Details' },
    ];

    const fetchDetail = (f) => {
        setLoading(true);
        setUnsupportedGateway(null);

        dispatch(getPaymentSourceDetail({ ...f, id })).then((res) => {
            if (res.error) {
                const payload = res.payload;
                if (payload?.message && payload.message.startsWith('Unsupported gateway')) {
                    setUnsupportedGateway(payload.message);
                } else {
                    toast.error(payload?.message || 'Failed to load payout details');
                }
            }
            setLoading(false);
        });
    };

    const getReport = () => {
        if (JSON.stringify(filter) === JSON.stringify(lastFilters)) return;
        setLastFilters({ ...filter });
        fetchDetail(filter);
    };

    useEffect(() => {
        getReport();
    }, [filter]);

    const onPageChange = (page) => setFilter(prev => ({ ...prev, page }));

    const onPageSizeChange = (newPageSize) => {
        setFilter(prev => ({
            ...prev,
            page: 1,
            pageSize: newPageSize === 'All' ? payoutReportState.paymentSourceDetailTableRow : newPageSize,
        }));
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        clearTimeout(searchTimer);
        setSearchTimer(setTimeout(() => {
            setFilter(prev => ({ ...prev, page: 1, search: value }));
        }, 500));
    };

    const handleTypeChange = (option) => {
        setTypeFilter(option);
        setFilter(prev => ({ ...prev, page: 1, type: option ? option.value : null }));
    };

    const handleMatchStatusChange = (option) => {
        setMatchStatusFilter(option);
        setFilter(prev => ({ ...prev, page: 1, match_status: option ? option.value : null }));
    };

    const handleDateMatchStatusChange = (option) => {
        setDateMatchStatusFilter(option);
        setFilter(prev => ({ ...prev, page: 1, date_match_status: option ? option.value : null }));
    };

    const handleRecheck = () => {
        setRecheckLoading(true);
        dispatch(recheckPayoutLines(id))
            .then((res) => {
                if (res.error) {
                    toast.error(res.payload?.error || res.error?.message || 'Re-check failed');
                } else {
                    toast.success('Lines re-verified. Refreshing data…');
                    // Reset to page 1 and force a fresh fetch
                    setLastFilters(null);
                    setFilter(prev => ({ ...prev, page: 1 }));
                }
            })
            .finally(() => setRecheckLoading(false));
    };

    const exportFile = () => {
        if (exportBtnDisabled) return;
        setExportBtnDisabled(true);

        dispatch(exportPaymentSourceDetail({ ...filter, id })).then((res) => {
            if (res.error) {
                toast.error('Export failed');
                setExportBtnDisabled(false);
                return;
            }
            const link = document.createElement('a');
            link.href = res.payload;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            link.remove();
            setExportBtnDisabled(false);
        });
    };

    const header = payoutReportState.paymentSourceDetailHeader;
    const totalSummary = payoutReportState.paymentSourceDetailTotalSummary;
    const totalRow = payoutReportState.paymentSourceDetailTableRow;

    const totalCharge    = Number(totalSummary?.total_charge_amount || 0);
    const totalRefund    = Number(totalSummary?.total_refund_amount || 0);
    const totalChargeFee = Number(totalSummary?.total_charge_fee || 0);
    const totalRefundFee = Number(totalSummary?.total_refund_fee || 0);

    const renderStatusBadge = (status, cfg, checkedAt) => {
        const { color, label } = cfg[status] || { color: 'secondary', label: status };
        return (
            <div>
                <span className={`badge badge-${color}`}>{label}</span>
                {checkedAt && (
                    <div><small className="text-muted" style={{ fontSize: 10 }}>{moment(checkedAt).format('DD-MMM HH:mm')}</small></div>
                )}
            </div>
        );
    };

    const tableColumns = [
        {
            name: 'Transaction Date',
            selector: row => row.transaction_date,
            left: true,
        },
        {
            name: 'Order Name',
            selector: row => row.order_id
                ? <Link className="text-primary" to={`${process.env.PUBLIC_URL}/order/${row.order_id}/view`} target="_blank">{row.order_name}</Link>
                : row.order_name,
            left: true,
        },
        {
            name: 'Shopify Order ID',
            selector: row => row.shopify_order_id || '-',
            left: true,
        },
        {
            name: 'Customer',
            selector: row => row.customer_name,
            left: true,
        },
        {
            name: 'Financial Status',
            selector: row => row.financial_status
                ? <span className="badge badge-secondary text-capitalize">{row.financial_status}</span>
                : '-',
            center: true,
        },
        {
            name: 'Type',
            selector: row => row.type,
            center: true,
        },
        {
            name: 'Type 2',
            selector: row => row.type_2 || '-',
            center: true,
        },
        {
            name: 'Source Type',
            selector: row => row.source_type || '-',
            center: true,
        },
        {
            name: 'Amount',
            selector: row => {
                const val = Number(row.amount);
                return (
                    <span className={val < 0 ? 'text-danger' : ''}>
                        {val.toFixed(2)}
                    </span>
                );
            },
            right: true,
        },
        {
            name: 'Txn Currency',
            selector: row => row.transaction_currency,
            center: true,
        },
        {
            name: 'Rate',
            selector: row => row.currency_rate,
            center: true,
        },
        {
            name: 'Settlement',
            selector: row => row.currency_code,
            center: true,
        },
        {
            name: 'Charge Fee',
            selector: row => (
                <span>
                    {Number(row.charge_fee).toFixed(2)}
                    {row.charge_fee_currency_code ? <small className="text-muted ml-1">{row.charge_fee_currency_code}</small> : null}
                </span>
            ),
            right: true,
        },
        {
            name: 'Match Status',
            selector: row => {
                if (!row.match_status) return <small className="text-muted">Not checked</small>;
                return renderStatusBadge(row.match_status, MATCH_STATUS_CFG, row.match_checked_at);
            },
            center: true,
            minWidth: '130px',
        },
        {
            name: 'Internal Amt',
            selector: row => {
                if (row.match_expected_amount == null) return '-';
                return (
                    <span className={row.match_status === 'amount_mismatch' ? 'text-warning font-weight-bold' : ''}>
                        {Number(row.match_expected_amount).toFixed(2)}
                    </span>
                );
            },
            right: true,
        },
        {
            name: 'Difference (GBP)',
            selector: row => {
                if (row.match_expected_amount == null || row.match_status === 'matched') return '-';
                // Both match_expected_amount and amount are in GBP settlement currency
                const expected = Math.abs(Number(row.match_expected_amount));
                const actual   = Math.abs(Number(row.amount));
                const diff     = expected - actual;
                return <span className={Math.abs(diff) > 0 ? 'text-danger' : 'text-muted'}>{diff.toFixed(2)}</span>;
            },
            right: true,
        },
        {
            name: 'Date Status',
            selector: row => {
                if (!row.date_match_status) return <small className="text-muted">-</small>;
                return renderStatusBadge(row.date_match_status, DATE_MATCH_STATUS_CFG, row.match_checked_at);
            },
            center: true,
            minWidth: '130px',
        },
        {
            name: 'Expected Date',
            selector: row => {
                if (row.match_expected_date == null) return '-';
                return (
                    <span className={row.date_match_status === 'date_mismatch' ? 'text-warning font-weight-bold' : ''}>
                        {moment(row.match_expected_date).format('DD-MMM-YYYY')}
                    </span>
                );
            },
            center: true,
        },
    ];

    const customStyles = {
        control: (base) => ({ ...base, minHeight: 50, border: 'none' }),
    };

    return (
        <Fragment>
            <Breadcrumb title="Payment Source Details" data={dataMenu} />
            <Container fluid className="finances-summary-page feature-products">

                {/* Filters */}
                <Card className="px-3">
                    <Row className="d-flex align-items-center">
                        <Col md="3">
                            <Form className="p-3">
                                <FormGroup className="m-0">
                                    <Input
                                        type="text"
                                        placeholder="Search by order name / number"
                                        onChange={handleSearchChange}
                                    />
                                </FormGroup>
                            </Form>
                        </Col>
                        <Col md="3">
                            <Select
                                className="select2-filter"
                                styles={customStyles}
                                placeholder="Filter by Type"
                                getOptionLabel={o => o.label}
                                getOptionValue={o => o.value}
                                value={typeFilter}
                                onChange={handleTypeChange}
                                isClearable
                                components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: (props) =>
                                        typeFilter ? null : <components.DropdownIndicator {...props} />,
                                }}
                                options={TYPE_OPTIONS}
                            />
                        </Col>
                        <Col md="3">
                            <Select
                                className="select2-filter"
                                styles={customStyles}
                                placeholder="Filter by Match Status"
                                getOptionLabel={o => o.label}
                                getOptionValue={o => o.value}
                                value={matchStatusFilter}
                                onChange={handleMatchStatusChange}
                                isClearable
                                components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: (props) =>
                                        matchStatusFilter ? null : <components.DropdownIndicator {...props} />,
                                }}
                                options={MATCH_STATUS_OPTIONS}
                            />
                        </Col>
                        <Col md="3">
                            <Select
                                className="select2-filter"
                                styles={customStyles}
                                placeholder="Filter by Date Match Status"
                                getOptionLabel={o => o.label}
                                getOptionValue={o => o.value}
                                value={dateMatchStatusFilter}
                                onChange={handleDateMatchStatusChange}
                                isClearable
                                components={{
                                    IndicatorSeparator: () => null,
                                    DropdownIndicator: (props) =>
                                        dateMatchStatusFilter ? null : <components.DropdownIndicator {...props} />,
                                }}
                                options={DATE_MATCH_STATUS_OPTIONS}
                            />
                        </Col>
                    </Row>
                    <Row className="pb-2 px-3 justify-content-end" style={{ gap: 8 }}>
                        <Button
                            type="button"
                            color="info"
                            onClick={handleRecheck}
                            disabled={recheckLoading || loading}
                        >
                            <i className={recheckLoading ? 'fa fa-spinner fa-spin mr-1' : 'fa fa-refresh mr-1'} />
                            {recheckLoading ? 'Re-checking…' : 'Re-check Lines'}
                        </Button>
                        <Button
                            type="button"
                            color="primary"
                            onClick={exportFile}
                            disabled={exportBtnDisabled}
                        >
                            <i className={exportBtnDisabled ? 'fa fa-spinner fa-spin mr-1' : 'fa fa-download mr-1'} />
                            Export Report
                        </Button>
                    </Row>
                </Card>

                {/* Payout Header Summary */}
                {header && (
                    <Card className="px-3 mb-3">
                        <CardBody>
                            <Row>
                                <Col>
                                    <small className="text-muted d-block">Payout Date</small>
                                    <strong>{header.issued_at ? moment(header.issued_at).format('DD-MMM-YYYY') : '-'}</strong>
                                </Col>
                                <Col>
                                    <small className="text-muted d-block">Status</small>
                                    <span className={`badge badge-${header.status === 'Paid' ? 'success' : 'warning'}`}>{header.status}</span>
                                </Col>
                                <Col>
                                    <small className="text-muted d-block">Payout Amount</small>
                                    <strong>{Number(header.amount).toFixed(2)}</strong>
                                </Col>
                                <Col>
                                    <small className="text-muted d-block">Charge Fee</small>
                                    <strong className="text-danger">{Number(header.charge_fee).toFixed(2)}</strong>
                                </Col>
                                <Col>
                                    <small className="text-muted d-block">Chargeback Amount</small>
                                    <strong className="text-danger">{Number(header.chargeback_amount || 0).toFixed(2)}</strong>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Match Summary */}
                {!unsupportedGateway && matchSummary && matchSummary.total > 0 && (
                    <Card className={`px-3 mb-3 border-left border-left-3 ${matchSummary.unmatched_count > 0 || matchSummary.mismatch_count > 0 ? 'border-danger' : 'border-success'}`}>
                        <CardBody className="py-3">
                            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
                                <strong className="text-muted" style={{ fontSize: 13 }}>Order Match Verification</strong>
                                <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                                    <span>
                                        <span className="badge badge-success mr-1">{matchSummary.matched_count}</span>
                                        <small>Matched</small>
                                    </span>
                                    <span>
                                        <span className={`badge badge-${matchSummary.mismatch_count > 0 ? 'warning' : 'secondary'} mr-1`}>{matchSummary.mismatch_count}</span>
                                        <small>Amount Mismatch</small>
                                    </span>
                                    <span>
                                        <span className={`badge badge-${matchSummary.unmatched_count > 0 ? 'danger' : 'secondary'} mr-1`}>{matchSummary.unmatched_count}</span>
                                        <small>Unmatched</small>
                                    </span>
                                    {matchSummary.unchecked_count > 0 && (
                                        <span>
                                            <span className="badge badge-secondary mr-1">{matchSummary.unchecked_count}</span>
                                            <small>Not Checked</small>
                                        </span>
                                    )}
                                    {matchSummary.total_discrepancy > 0 && (
                                        <span className="text-danger font-weight-bold">
                                            <small>Total Discrepancy: </small>
                                            {Number(matchSummary.total_discrepancy).toFixed(2)}
                                        </span>
                                    )}
                                </div>
                                {(matchSummary.unmatched_count > 0 || matchSummary.mismatch_count > 0) && (
                                    <small className="text-danger"><i className="fa fa-exclamation-triangle mr-1" />Payment generation is blocked until all lines match.</small>
                                )}
                                {matchSummary.unchecked_count > 0 && (
                                    <small className="text-warning"><i className="fa fa-clock-o mr-1" />{matchSummary.unchecked_count} line(s) not yet verified — click Re-check Lines.</small>
                                )}
                            </div>
                            <div className="d-flex align-items-center justify-content-between flex-wrap mt-2" style={{ gap: 12 }}>
                                <strong className="text-muted" style={{ fontSize: 13 }}>Date Match Verification</strong>
                                <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                                    <span>
                                        <span className="badge badge-success mr-1">{matchSummary.date_matched_count}</span>
                                        <small>Matched</small>
                                    </span>
                                    <span>
                                        <span className={`badge badge-${matchSummary.date_mismatch_count > 0 ? 'warning' : 'secondary'} mr-1`}>{matchSummary.date_mismatch_count}</span>
                                        <small>Date Mismatch</small>
                                    </span>
                                    <span>
                                        <span className={`badge badge-${matchSummary.date_unmatched_count > 0 ? 'danger' : 'secondary'} mr-1`}>{matchSummary.date_unmatched_count}</span>
                                        <small>Unmatched</small>
                                    </span>
                                    {matchSummary.date_unchecked_count > 0 && (
                                        <span>
                                            <span className="badge badge-secondary mr-1">{matchSummary.date_unchecked_count}</span>
                                            <small>Not Checked</small>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Totals Summary */}
                {!unsupportedGateway && (
                    <div className="row mb-3" style={{ marginLeft: -6, marginRight: -6 }}>
                        {[
                            { label: 'Total Records', value: totalRow },
                            { label: 'Total Charge', value: totalCharge.toFixed(2) },
                            { label: 'Total Refund', value: totalRefund.toFixed(2) },
                            { label: 'Total Fees', value: (0 - totalChargeFee - totalRefundFee).toFixed(2) },
                            { label: 'Net', value: (totalCharge + totalRefund - totalChargeFee - totalRefundFee).toFixed(2) },
                        ].map((w, i) => (
                            <div key={i} className="col-md-6 col-xl" style={{ padding: 6 }}>
                                <div className="summary-box">
                                    <div className="summary-box-body text-right">
                                        <h6 className="text-truncate sb-title">{w.label}</h6>
                                        <h5 className="sm-value">{w.value}</h5>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Unsupported gateway message */}
                {unsupportedGateway && (
                    <Card className="px-3">
                        <CardBody className="text-center py-5">
                            <i className="fa fa-exclamation-triangle fa-2x text-warning mb-3" />
                            <h5>{unsupportedGateway}</h5>
                            <p className="text-muted">Order-level details are only available for Shopify gateway payouts.</p>
                        </CardBody>
                    </Card>
                )}

                {/* Grid */}
                {!unsupportedGateway && (
                    <Card className="px-3">
                        <CardBody>
                            <BootstrapTable
                                headerColumns={tableColumns}
                                gridData={payoutReportState.paymentSourceDetailLists}
                                noAction
                                progressPending={loading}
                                paginationServer
                                paginationTotalRows={payoutReportState.paymentSourceDetailTableRow}
                                currentPage={filter.page}
                                perPage={filter.pageSize}
                                onChangePage={onPageChange}
                                onChangeRowsPerPage={onPageSizeChange}
                                size="md"
                                noDataComponent={
                                    loading ? null : (
                                        <div className="text-center py-5 text-muted">
                                            {filter.search || filter.type || filter.match_status || filter.date_match_status
                                                ? 'No records match your search or filter.'
                                                : 'No transaction lines found for this payout.'}
                                        </div>
                                    )
                                }
                            />
                        </CardBody>
                    </Card>
                )}
            </Container>
        </Fragment>
    );
};

export default withReducer([{ report: reportReducer }])(PaymentSourceDetails);
